import {
  faArrowsRotate,
  faCheck,
  faCircleCheck,
  faForward,
  faLink,
  faListCheck,
  faUpRightFromSquare,
  faWrench,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ProgressBar } from 'react-bootstrap';
import { useToasts } from 'react-toast-notifications';
import { fetcher } from 'src/api/fetcher';
import { deleteInstructionReport, saveInstructionReport } from 'src/api/maintenance';
import Loader from 'src/components/loader';
import MultilineText from 'src/components/multiline-text';
import BadgeRow from 'src/components/ui/BadgeRow';
import Button from 'src/components/ui/Button';
import EmptyState from 'src/components/ui/EmptyState';
import ListTable from 'src/components/ui/ListTable';
import MetaLine from 'src/components/ui/MetaLine';
import StatusBadge from 'src/components/ui/StatusBadge';
import { MAINTENANCE_SCOPE } from 'src/constants/maintenance';
import { useResolvedRequirementsByUris } from 'src/hooks/services/useMaintenance';
import {
  API_RESOURCES,
  PAGINATION_IGNORE_DEFAULT_LIMIT,
  WORK_INSTRUCTION_DOCUMENT_TYPES,
} from 'src/utils/constants';
import {
  areRequiredInstructionsAnswered,
  getRequirementInstructions,
  groupReportsByFulfillment,
  hasBlockingThresholdErrors,
  hasPartiallyFilledRange,
  isInstructionAnswered,
  isThresholdErrorBlocking,
  normalizeReportFile,
  parseReportRowValue,
  reportMatchesInstruction,
  summarizeFrequency,
} from 'src/utils/maintenance';
import { getUuid } from 'src/utils/url';

import InstructionInput from './InstructionInput';

const emptyItemState = () => ({
  open: false,
  skipped: false,
  values: {},
  files: {},
  uploading: {},
});

const saveStateKey = (fulfillmentUri, instructionUuid) => `${fulfillmentUri}:${instructionUuid}`;

/** Step number tile: red ✗ on an Error-threshold violation, green ✓ when done. */
const StepMark = ({ blocked, done, index }) => {
  if (blocked) {
    return (
      <span className='mnt-step-row__num mnt-step-row__num--error'>
        <FontAwesomeIcon icon={faXmark} />
      </span>
    );
  }
  if (done) {
    return (
      <span className='mnt-step-row__num mnt-step-row__num--done'>
        <FontAwesomeIcon icon={faCheck} />
      </span>
    );
  }
  return <span className='mnt-step-row__num'>{index + 1}</span>;
};

StepMark.propTypes = {
  blocked: PropTypes.bool.isRequired,
  done: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
};

const RunEvent = ({
  event,
  eventName,
  fulfillments,
  requirementByUri,
  onViewFile,
  onReviewComplete,
  onBack,
}) => {
  const { addToast } = useToasts();

  // A requirement archived/superseded mid-event drops out of the active map;
  // resolve those pinned revisions so their fulfillments still appear (and get
  // completed/skipped) instead of being silently dropped from the run.
  const missingRequirementUris = useMemo(
    () =>
      fulfillments
        .map(fulfillment => fulfillment.maintenance_requirement)
        .filter(uri => uri && !requirementByUri[uri]),
    [fulfillments, requirementByUri]
  );
  const { data: resolvedByUri = {}, isInitialLoading: resolvingRequirements } =
    useResolvedRequirementsByUris(missingRequirementUris);
  const mergedRequirementByUri = useMemo(
    () => ({ ...resolvedByUri, ...requirementByUri }),
    [resolvedByUri, requirementByUri]
  );

  const items = useMemo(
    () =>
      fulfillments
        .map(fulfillment => {
          const requirement = mergedRequirementByUri[fulfillment.maintenance_requirement];
          if (!requirement) return null;
          return {
            fulfillment,
            requirement,
            instructions: getRequirementInstructions(requirement),
            isInstance: requirement.scope === MAINTENANCE_SCOPE.INSTANCE,
          };
        })
        .filter(Boolean),
    [fulfillments, mergedRequirementByUri]
  );

  const [state, setState] = useState(() => {
    const initial = {};
    items.forEach((item, index) => {
      initial[item.fulfillment.uri] = { ...emptyItemState(), open: index === 0 };
    });
    return initial;
  });

  // reportUuid (per fulfillment+instruction) lives in a ref so the save loop
  // always reads the freshest value and never re-creates an existing report.
  const reportUuidsRef = useRef({});
  const saveLocks = useRef({});
  const seededRef = useRef(false);
  // Gate interaction until the seed round-trip resolves, so an answer entered
  // mid-seed can't be clobbered by the (report-existence-blind) seed replace.
  const [seedReady, setSeedReady] = useState(false);
  const [savingByKey, setSavingByKey] = useState({});
  const [failedByKey, setFailedByKey] = useState({});
  const [isReviewing, setIsReviewing] = useState(false);

  useEffect(() => {
    if (seededRef.current || resolvingRequirements) return;
    seededRef.current = true;
    if (!items.length) {
      setSeedReady(true);
      return undefined;
    }
    let cancelled = false;

    const seedFromSavedReports = async () => {
      const seeded = {};
      try {
        const response = await fetcher(API_RESOURCES.MAINTENANCE_INSTRUCTION_REPORT, {
          params: {
            'filter[maintenance_event_requirement_fulfillment]': items
              .map(item => item.fulfillment.uri)
              .join(','),
            'page[limit]': PAGINATION_IGNORE_DEFAULT_LIMIT,
          },
        });
        const reportsByFulfillment = groupReportsByFulfillment(response.resources || []);
        items.forEach(item => {
          const itemState = { ...emptyItemState() };
          const byInstruction = new Map(
            item.instructions.map(instruction => [instruction.uri, instruction])
          );
          (reportsByFulfillment[item.fulfillment.uri] || []).forEach(report => {
            const instruction =
              byInstruction.get(report.work_instruction) ||
              item.instructions.find(itemInstruction =>
                reportMatchesInstruction(report, itemInstruction)
              );
            if (!instruction) return;
            reportUuidsRef.current[saveStateKey(item.fulfillment.uri, instruction.uuid)] = getUuid(
              report.uri
            );
            if (report.report_file) {
              const reportFile = normalizeReportFile(report.report_file);
              itemState.files[instruction.uuid] = reportFile;
              itemState.values[instruction.uuid] = reportFile;
            } else {
              itemState.values[instruction.uuid] = parseReportRowValue(
                instruction.reportType,
                report
              );
            }
          });
          seeded[item.fulfillment.uri] = itemState;
        });
      } catch {
        items.forEach(item => {
          seeded[item.fulfillment.uri] = { ...emptyItemState() };
        });
      }
      if (cancelled) return;
      const firstOpen = items.find(
        item =>
          !areRequiredInstructionsAnswered(item.instructions, seeded[item.fulfillment.uri].values)
      );
      if (firstOpen) seeded[firstOpen.fulfillment.uri].open = true;
      else if (items[0]) seeded[items[0].fulfillment.uri].open = true;
      setState(seeded);
      setSeedReady(true);
    };

    seedFromSavedReports();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fulfillments.length, resolvingRequirements]);

  const patchItem = (uuid, patch) =>
    setState(prev => ({ ...prev, [uuid]: { ...prev[uuid], ...patch } }));

  const setValue = (uuid, instructionUuid, value) =>
    setState(prev => ({
      ...prev,
      [uuid]: { ...prev[uuid], values: { ...prev[uuid].values, [instructionUuid]: value } },
    }));

  const setUploading = (uuid, instructionUuid, percent) =>
    setState(prev => ({
      ...prev,
      [uuid]: {
        ...prev[uuid],
        uploading: { ...prev[uuid].uploading, [instructionUuid]: percent },
      },
    }));

  const setSavePending = (key, pending) =>
    setSavingByKey(prev => {
      if (pending) return { ...prev, [key]: true };
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const setSaveFailed = (key, failed) =>
    setFailedByKey(prev => {
      if (failed) return { ...prev, [key]: true };
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const persist = (item, instruction, value) => {
    // Never send a value blocked by an Error-action threshold - the backend
    // rejects it anyway. It stays local, behind the form's own error display,
    // until corrected.
    if (isThresholdErrorBlocking(instruction, value)) return null;

    const key = saveStateKey(item.fulfillment.uri, instruction.uuid);
    const lock = saveLocks.current[key] || (saveLocks.current[key] = { busy: false, next: null });
    lock.next = { value };
    lock.failed = false;
    setSaveFailed(key, false);
    if (lock.promise) return lock.promise;

    const runSaveQueue = async () => {
      lock.busy = true;
      setSavePending(key, true);
      try {
        while (lock.next) {
          const { value: current } = lock.next;
          lock.next = null;

          const isFile = WORK_INSTRUCTION_DOCUMENT_TYPES.includes(instruction.reportType);
          const file = isFile && current instanceof File ? current : null;
          if (isFile && !file) continue; // an already-saved attachment; nothing to re-upload

          if (file) setUploading(item.fulfillment.uri, instruction.uuid, 0);

          const saved = await saveInstructionReport(
            {
              fulfillmentUri: item.fulfillment.uri,
              instruction,
              value: current,
              reportUuid: reportUuidsRef.current[key],
              file,
              onUploadProgress: percent =>
                setUploading(item.fulfillment.uri, instruction.uuid, Math.round(percent * 100)),
              onReportCreated: createdUuid => {
                reportUuidsRef.current[key] = createdUuid;
              },
            },
            addToast
          );

          if (saved?.uuid) reportUuidsRef.current[key] = saved.uuid;
          if (file) {
            setState(prev => ({
              ...prev,
              [item.fulfillment.uri]: {
                ...prev[item.fulfillment.uri],
                uploading: { ...prev[item.fulfillment.uri].uploading, [instruction.uuid]: null },
                files: {
                  ...prev[item.fulfillment.uri].files,
                  [instruction.uuid]: saved?.reportFile || { filename: file.name },
                },
              },
            }));
          }
        }
        lock.failed = false;
        setSaveFailed(key, false);
      } catch {
        lock.failed = true;
        setSaveFailed(key, true);
      } finally {
        lock.busy = false;
        lock.promise = null;
        setSavePending(key, false);
      }
    };

    lock.promise = runSaveQueue();
    return lock.promise;
  };

  // Clear a non-required attachment: remove the report row, forget its uuid so
  // a re-upload POSTs a fresh one, and drop the local value/file.
  const clearFile = async (item, instruction) => {
    const key = saveStateKey(item.fulfillment.uri, instruction.uuid);
    const uuid = reportUuidsRef.current[key];
    try {
      if (uuid) await deleteInstructionReport(uuid, addToast);
      delete reportUuidsRef.current[key];
      setState(prev => {
        const itemState = prev[item.fulfillment.uri];
        const values = { ...itemState.values };
        const files = { ...itemState.files };
        delete values[instruction.uuid];
        delete files[instruction.uuid];
        return { ...prev, [item.fulfillment.uri]: { ...itemState, values, files } };
      });
    } catch {
      /* toast surfaced by the api layer */
    }
  };

  if (!seedReady) return <Loader />;

  // Done = answered without an Error-threshold violation. Drives the
  // checkmark, the badges, and the progress bar alike.
  const isStepDone = (instruction, itemState) => {
    const value = itemState.values[instruction.uuid];
    return (
      isInstructionAnswered(instruction.reportType, value) &&
      !isThresholdErrorBlocking(instruction, value)
    );
  };

  const overall = items.reduce(
    (acc, item) => {
      const itemState = state[item.fulfillment.uri] || emptyItemState();
      const total = item.instructions.length;
      const done = item.instructions.filter(instruction =>
        isStepDone(instruction, itemState)
      ).length;
      acc.total += total;
      acc.done += itemState.skipped ? total : done;
      return acc;
    },
    { total: 0, done: 0 }
  );

  // Skipped is always handled. Otherwise: every required step answered, no
  // half-entered range, no Error-threshold violation - the same gate as runs.
  const isHandled = item => {
    const itemState = state[item.fulfillment.uri] || emptyItemState();
    if (itemState.skipped) return true;
    return (
      areRequiredInstructionsAnswered(item.instructions, itemState.values) &&
      !hasPartiallyFilledRange(item.instructions, itemState.values) &&
      !hasBlockingThresholdErrors(item.instructions, itemState.values)
    );
  };

  const allHandled = items.length > 0 && items.every(isHandled);
  const hasPendingSaves = Object.keys(savingByKey).length > 0;
  const hasFailedSaves = Object.keys(failedByKey).length > 0;

  const waitForPendingSaves = async () => {
    const pending = Object.values(saveLocks.current)
      .map(lock => lock.promise)
      .filter(Boolean);
    if (pending.length) await Promise.allSettled(pending);
  };

  const persistAnsweredValues = async () => {
    const saves = [];
    items.forEach(item => {
      const itemState = state[item.fulfillment.uri] || emptyItemState();
      if (itemState.skipped) return;
      item.instructions.forEach(instruction => {
        const value = itemState.values[instruction.uuid];
        if (!isInstructionAnswered(instruction.reportType, value)) return;
        saves.push(persist(item, instruction, value));
      });
    });
    await Promise.allSettled(saves.filter(Boolean));
    await waitForPendingSaves();
    return Object.values(saveLocks.current).some(lock => lock.failed);
  };

  const handleReview = async () => {
    setIsReviewing(true);
    const hasFailedSave = await persistAnsweredValues();
    setIsReviewing(false);
    if (hasFailedSave) {
      addToast('Some answers could not be saved. Please retry before completing.', {
        appearance: 'error',
      });
      return;
    }
    onReviewComplete(
      items.map(item => {
        const itemState = state[item.fulfillment.uri] || emptyItemState();
        return {
          fulfillment: item.fulfillment,
          requirement: item.requirement,
          instructions: item.instructions,
          values: itemState.values,
          files: itemState.files,
          skipped: itemState.skipped || false,
        };
      })
    );
  };

  return (
    <div>
      <div className='mnt-run-title'>{eventName}</div>
      <BadgeRow center>
        <StatusBadge dot size='sm' uppercase={false} variant='warning'>
          In progress
        </StatusBadge>
        {event.downtime && (
          <StatusBadge size='sm' uppercase={false} variant='warning' icon={faLink}>
            Downtime linked
          </StatusBadge>
        )}
      </BadgeRow>

      <div className='mnt-progress'>
        <div className='mnt-progress__label'>
          <span>
            <FontAwesomeIcon icon={faListCheck} className='spacer-right' /> Overall progress
          </span>
          <span className='mnt-progress__done'>
            {overall.done} of {overall.total} done
          </span>
        </div>
        <ProgressBar
          now={overall.total ? Math.round((overall.done / overall.total) * 100) : 0}
          style={{ height: 8 }}
        />
      </div>

      {items.map(item => {
        const itemState = state[item.fulfillment.uri] || emptyItemState();
        const answered = item.instructions.filter(instruction =>
          isStepDone(instruction, itemState)
        ).length;
        // The Done badge reflects what the operator actually recorded (every step
        // answered) — NOT the completion gate. A requirement with only optional
        // steps is completable untouched (isHandled), but must not read as Done.
        const total = item.instructions.length;
        const complete = !itemState.skipped && total > 0 && answered === total;

        let badge;
        if (itemState.skipped) {
          badge = (
            <StatusBadge size='sm' uppercase={false} icon={faForward}>
              Skipped
            </StatusBadge>
          );
        } else if (total === 0) {
          badge = (
            <StatusBadge size='sm' uppercase={false}>
              No steps
            </StatusBadge>
          );
        } else if (complete) {
          badge = (
            <StatusBadge size='sm' uppercase={false} variant='success' icon={faCheck}>
              Done
            </StatusBadge>
          );
        } else {
          badge = (
            <StatusBadge size='sm' uppercase={false} variant='info'>
              {answered}/{total}
            </StatusBadge>
          );
        }

        const header = (
          <>
            <span
              className={`mnt-scope-tile ${
                item.isInstance ? 'mnt-scope-tile--instance' : 'mnt-scope-tile--type'
              }`}
            >
              <FontAwesomeIcon icon={complete ? faCheck : faWrench} />
            </span>
            <div className='mnt-run-head'>
              <div className='mnt-run-head__name'>{item.requirement.name}</div>
              <MetaLine icon={faArrowsRotate}>{summarizeFrequency(item.requirement)}</MetaLine>
            </div>
            <span className='mnt-run-head__badge'>{badge}</span>
          </>
        );

        return (
          <ListTable
            key={item.fulfillment.uri}
            isExpanded={itemState.open}
            headerContent={header}
            onToggle={() => patchItem(item.fulfillment.uri, { open: !itemState.open })}
          >
            <div className='mnt-section-body'>
              {itemState.skipped ? (
                <EmptyState variant='note'>
                  Skipped. No reports will be recorded.
                  <Button
                    variant='link'
                    size='small'
                    onClick={() => patchItem(item.fulfillment.uri, { skipped: false })}
                  >
                    Undo
                  </Button>
                </EmptyState>
              ) : (
                <>
                  {!item.instructions.length && (
                    <EmptyState variant='note'>
                      This requirement has no checklist steps. There is nothing to record; it counts
                      as done once you complete the event.
                    </EmptyState>
                  )}
                  {item.instructions.map((instruction, index) => {
                    const fUri = item.fulfillment.uri;
                    const value = itemState.values[instruction.uuid];
                    const blocked = isThresholdErrorBlocking(instruction, value);
                    const done = isInstructionAnswered(instruction.reportType, value) && !blocked;
                    return (
                      <div key={instruction.uuid} className='mnt-instruction'>
                        <div className='mnt-instruction__head'>
                          <StepMark blocked={blocked} done={done} index={index} />
                          <div className='mnt-instruction__q'>
                            <MultilineText>{instruction.question}</MultilineText>
                            {instruction.required && (
                              <span className='mnt-instruction__req'>*</span>
                            )}
                            {instruction.additionalInstructionUrl && (
                              <a
                                className='mnt-instruction__ext'
                                href={instruction.additionalInstructionUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                aria-label='Additional instructions'
                                onClick={event => event.stopPropagation()}
                              >
                                <FontAwesomeIcon icon={faUpRightFromSquare} />
                              </a>
                            )}
                          </div>
                        </div>
                        <div className='mnt-instruction__input'>
                          <InstructionInput
                            instruction={instruction}
                            value={value}
                            reportFile={itemState.files[instruction.uuid]}
                            uploading={itemState.uploading[instruction.uuid]}
                            onViewFile={onViewFile}
                            onChange={next => setValue(fUri, instruction.uuid, next)}
                            onCommit={next => persist(item, instruction, next)}
                            onClear={() => clearFile(item, instruction)}
                          />
                        </div>
                      </div>
                    );
                  })}
                  <Button
                    block
                    variant='default'
                    icon={<FontAwesomeIcon icon={faForward} />}
                    onClick={() => patchItem(item.fulfillment.uri, { skipped: true })}
                  >
                    Skip this requirement
                  </Button>
                </>
              )}
            </div>
          </ListTable>
        );
      })}

      <Button
        block
        fill={allHandled}
        variant={allHandled ? 'success' : 'default'}
        size='large'
        className='mt-2'
        disabled={!allHandled || isReviewing || hasPendingSaves}
        loading={isReviewing || hasPendingSaves}
        icon={<FontAwesomeIcon icon={faCircleCheck} />}
        onClick={handleReview}
      >
        {isReviewing || hasPendingSaves
          ? 'Saving…'
          : hasFailedSaves
            ? 'Retry save & review'
            : 'Review & complete'}
      </Button>
      <Button block variant='link' className='mt-1' onClick={onBack}>
        Back
      </Button>
    </div>
  );
};

RunEvent.propTypes = {
  event: PropTypes.object.isRequired,
  eventName: PropTypes.string,
  fulfillments: PropTypes.array.isRequired,
  requirementByUri: PropTypes.object.isRequired,
  onViewFile: PropTypes.func,
  onReviewComplete: PropTypes.func.isRequired,
  onBack: PropTypes.func,
};

RunEvent.defaultProps = { eventName: 'Maintenance event', onViewFile: () => {}, onBack: () => {} };

export default RunEvent;
